import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { spliceOut } from 'components/sharpr/util/spliceOut';
import { ensure } from '../../../sharpr/ensure/ensure.js';
import _ from 'lodash';

export function SsoRulesetsRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.SsoRulesets', {
		url: '/admin/sso/sps/:id',
		controllerAs: 'vm',
		controller: SsoRulesetsCtrl,
		templateUrl: '/ng/hub/Sso/Rulesets/SsoRulesetsCtrl.html?' + window.BUILD,
		permission: ['manage_hubs'],
		resolve: {
			sp: ApiResourcify.endpoint('/sso/sps/:id'),
			rulesets: ApiResourcify.endpoint('/sso/sps/:id/rulesets'),
			style: () => AssetManager.css('/ng/hub/Sso/Rulesets/SsoRulesetsCtrl.css'),
			$title: () => 'Edit SSO Configuration',
		},
	});
}

export class SsoRulesetsCtrl {
	constructor($scope, Api, Identity, modal, sp, rulesets) {
		'ngInject';
		// save injections
		this.Api = Api;
		this.Identity = Identity;
		this.modal = modal;
		// setup
		this.sp = sp;
		this.setupAllowances();
		this.rulesets = rulesets;
		this.fieldOptions = [
			{ id: null, name: 'Choose...' },
			{ id: 'domain', name: 'Email Domain' },
			{ id: 'division', name: 'Division' },
			{ id: 'organization', name: 'Organization' },
			{ id: 'email', name: 'Email Address' },
			{ id: 'any', name: '(Any user)' },
		];
		this.operatorOptions = [
			{ id: null, name: 'Choose...' },
			{ id: 'Equals', name: 'is' },
			{ id: 'NotEquals', name: 'is not' },
			{ id: 'OneOf', name: 'is one of' },
			{ id: 'NotOneOf', name: 'is not one of' },
			{ id: 'Contains', name: 'contains' },
			{ id: 'NotContains', name: 'does not contain' },
			{ id: 'BeginsWith', name: 'begins with' },
			{ id: 'EndsWith', name: 'ends with' },
			{ id: 'IsEmpty', name: 'is empty' },
			{ id: 'IsNotEmpty', name: 'is not empty' },
		];
		this.operatorLookup = {};
		this.operatorOptions.forEach(opt => {
			this.operatorLookup[opt.id] = opt.name;
		});
		this.roleList = [
			{ name: '', id: null },
			{ name: 'Editor/Intel Leader', id: 'intel leader' },
			{ name: 'Client', id: 'executive' },
			{ name: 'Member/View Only', id: 'member' },
			{ name: 'Reader', id: 'reader' },
		];
		this.stopOptions = [
			{ id: false, name: 'Check next condition' },
			{ id: true, name: 'Stop & ignore other rules' },
		];
		this.hubList = this.getHubList();
		this.isSuperadmin = this.Identity.isSuperadmin();
		if (this.sp.auth_type === 'SAML') {
			this.loginUrl = `${this.sp.base_url}/login?sso=${this.sp.sp}`;
		} else if (this.sp.auth_type === 'GSuite') {
			this.loginUrl = `${this.sp.base_url}/users/openid/${this.sp.sp}`;
		}
		$scope.$watch('vm.hubFilter', this.runHubFilter.bind(this));
	}

	getHubList() {
		let list = this.Identity.hubsInAccount(this.sp.account_id);
		if (Array.isArray(this.sp.connected_affiliations)) {
			// show hub list across multiple affiliations if the user has access to them
			this.sp.connected_affiliations.forEach(id => {
				list = list.concat(this.Identity.hubsInAccount(id));
			});
		}
		return list;
	}

	setupAllowances() {
		this.allowAdd = !this.sp.diff_strategy.includes('toRemove');
		this.allowUpgrade = !this.sp.diff_strategy.includes('toDowngrade');
		this.allowDowngrade = !this.sp.diff_strategy.includes('toUpgrade');
		this.allowRemove = !this.sp.diff_strategy.includes('toAdd');
		this.allowSetDefault = !this.sp.diff_strategy.includes('defaultHub');
	}

	saveAllowances() {
		const strategy = [
			this.allowAdd ? '' : 'toRemove',
			this.allowUpgrade ? '' : 'toDowngrade',
			this.allowDowngrade ? '' : 'toUpgrade',
			this.allowRemove ? '' : 'toAdd',
			this.allowSetDefault ? '' : 'defaultHub',
		]
			.filter(Boolean)
			.join(',');
		this.Api.patch(`/sso/sps/${this.sp.id}`, { diff_strategy: strategy });
	}

	toggleIsActive() {
		this.sp.is_active = !this.sp.is_active;
		this.Api.patch(`/sso/sps/${this.sp.id}`, { is_active: this.sp.is_active });
	}

	back() {
		if (this.isSuperadmin) {
			window.location.href = '#/admin/sso/admin';
		} else {
			window.location.href = '#/admin/sso/sps';
		}
	}

	discard() {
		this.modal
			.confirm({
				text: 'Are you sure you want to discard changes?',
				header: 'Are you sure?',
			})
			.then(
				() => this.back(),
				() => {}
			);
	}

	openStats() {
		this.modal.state('hub.loggedin.SsoStats', {
			heading: 'SSO Stats',
			width: 600,
			height: 800,
			params: { id: this.sp.id },
			locals: { $addConditions: this.addConditionsFromStats.bind(this) },
		});
	}

	addConditionsFromStats(field, values) {
		values.forEach(value => {
			this.rulesets.push({
				match_type: 'any',
				and_stop: true,
				make_admin: false,
				rules: [
					{
						field,
						operator: value === '' ? 'IsEmpty' : 'Equals',
						operand: value,
					},
				],
				roles: [],
			});
		});
	}

	addRule(ruleset) {
		if (ruleset.rules.length === 1) {
			ruleset.match_type = 'any';
		}
		ruleset.rules.push({
			field: '',
			operator: '',
			operand: '',
			make_admin: false,
			toAdd: true,
			toUpgrade: true,
			toDowngrade: false,
			toRemove: false,
		});
	}

	removeRuleset(ruleset) {
		spliceOut(this.rulesets, ruleset);
	}

	copyRuleset(ruleset) {
		this.rulesets.push(_.cloneDeep(ruleset));
	}

	moveRulesetDown(ruleset) {
		const idx = this.rulesets.indexOf(ruleset);
		const after = this.rulesets[idx + 1];
		if (!after) {
			return;
		}
		this.rulesets[idx] = after;
		this.rulesets[idx + 1] = ruleset;
	}

	moveRulesetUp(ruleset) {
		const idx = this.rulesets.indexOf(ruleset);
		const before = this.rulesets[idx - 1];
		if (!before) {
			return;
		}
		this.rulesets[idx] = before;
		this.rulesets[idx - 1] = ruleset;
	}

	removeRule(ruleset, rule) {
		spliceOut(ruleset.rules, rule);
	}

	addCondition() {
		let lastRuleset = this.rulesets[this.rulesets.length - 1];
		if (!lastRuleset) {
			lastRuleset = { rules: [{}] };
		}
		const lastRule = lastRuleset.rules[lastRuleset.rules.length - 1];
		this.rulesets.push({
			and_stop: true,
			match_type: 'all',
			make_admin: false,
			rules: [
				{
					field: lastRule.field || null,
					operator: lastRule.operator || null,
					operand: '',
				},
			],
			roles: [],
		});
	}

	clearConditions() {
		this.rulesets = [];
	}

	editRoles(ruleset) {
		this.hubFilter = '';
		this.runHubFilter();
		this.defaultHubId = null;
		this.roleChoices = {};
		ruleset.roles.forEach(role => {
			if (role.is_default) {
				this.defaultHubId = role.client_id;
			}
			this.roleChoices[role.client_id] = role.role;
		});
		this.chosenRuleset = ruleset;
	}

	runHubFilter() {
		const lowerFilter = (this.hubFilter || '').toLocaleLowerCase();
		this.filteredHubs = this.hubList.filter(hub => {
			return hub.name.toLocaleLowerCase().indexOf(lowerFilter) > -1;
		});
	}

	checkDefaultHub(hub) {
		if (this.defaultHubId == hub.id && !this.roleChoices[hub.id]) {
			this.defaultHubId = null;
		}
	}

	saveRoleChoices() {
		this.chosenRuleset.roles = [];
		_.forOwn(this.roleChoices, (roleName, hubId) => {
			const isDefault = this.defaultHubId == hubId;
			if (roleName) {
				this.chosenRuleset.roles.push({
					role: roleName,
					client_id: hubId,
					affiliation_id: this.sp.account_id,
					is_default: isDefault,
				});
			}
		});
		this.chosenRuleset = null;
	}

	preview() {
		const params = {
			rulesets: this.rulesets,
		};
		this.Api.post(`/sso/sps/${this.sp.id}/rulesets/preview`, params).then(data => {
			this.previewData = data;
			this.buildTotals();
		});
	}

	closePreview() {
		this.isPreviewingDetails = null;
		this.buildTotals();
	}

	buildTotals() {
		this.totals = {};
		this.totals.toAdd = 0;
		this.totals.toUpgrade = 0;
		this.totals.toDowngrade = 0;
		this.totals.toRemove = 0;
		this.totals.numUsers = 0;

		if (this.previewData.users && this.previewData.users instanceof Array) {
			this.previewData.users.forEach(user => {
				let hasAnyChanges;
				if (this.previewData.toAdd > 0) {
					ensure.array(user.changes.toAdd.hubs).forEach(hub => {
						if (hub.isSelected) {
							this.totals.toAdd++;
							hasAnyChanges = true;
						}
					});
					ensure.array(user.changes.toAdd.accounts).forEach(account => {
						if (account.isSelected) {
							this.totals.toAdd++;
							hasAnyChanges = true;
						}
					});
				}
				if (this.previewData.toUpgrade > 0) {
					ensure.array(user.changes.toUpgrade.hubs).forEach(hub => {
						if (hub.isSelected) {
							this.totals.toUpgrade++;
							hasAnyChanges = true;
						}
					});
				}
				if (this.previewData.toDowngrade > 0) {
					ensure.array(user.changes.toDowngrade.hubs).forEach(hub => {
						if (hub.isSelected) {
							this.totals.toDowngrade++;
							hasAnyChanges = true;
						}
					});
				}
				if (this.previewData.toRemove > 0) {
					ensure.array(user.changes.toRemove.hubs).forEach(hub => {
						if (hub.isSelected) {
							this.totals.toRemove++;
							hasAnyChanges = true;
						}
					});
					ensure.array(user.changes.toRemove.accounts).forEach(account => {
						if (account.isSelected) {
							this.totals.toRemove++;
							hasAnyChanges = true;
						}
					});
				}
				if (hasAnyChanges) {
					this.totals.numUsers++;
				}
			});
		}

		this.totals.overall = this.totals.toAdd + this.totals.toUpgrade + this.totals.toDowngrade + this.totals.toRemove;
	}

	saveAll({ updateUsers }) {
		const params = {
			rulesets: this.rulesets,
			users: updateUsers ? this.previewData.users : undefined,
		};
		this.Api.put(`/sso/sps/${this.sp.id}/rulesets`, params).then(() => {
			this.back();
		});
	}

	editDomains() {
		this.isEditingDomains = true;
		this.editedDomains = this.sp.domains.join('\n');
	}

	saveDomains() {
		this.isEditingDomains = false;
		this.sp.domains = this.editedDomains.split('\n').filter(Boolean);
		this.Api.put(`/sso/sps/${this.sp.id}/domains`, { domains: this.sp.domains });
	}

	editAttr() {
		this.isEditingAttr = true;
	}

	saveAttr() {
		this.isEditingAttr = false;
		this.Api.patch(`/sso/sps/${this.sp.id}`, {
			name_id_format: this.sp.name_id_format || '',
			name_id_policy: this.sp.name_id_policy || '',
			override_sso_ref: this.sp.override_sso_ref || '',
			override_fname: this.sp.override_fname || '',
			override_lname: this.sp.override_lname || '',
			override_email: this.sp.override_email || '',
			override_division: this.sp.override_division || '',
			override_organization: this.sp.override_organization || '',
			force_email_domain: this.sp.force_email_domain || '',
			privatekey: this.sp.privatekey || '',
			certificate: this.sp.certificate || '',
		});
	}

	editClientMetadata() {
		this.isEditingMetadata = true;
	}

	async saveClientMetadata() {
		if (this.sp.configs && this.sp.configs.length > 0) {
			const sure = `Obsolete records will be deleted for ${_.escape(this.sp.sp)}.`;
			const ok = await this.modal.confirm({ escapedHtml: sure });
			if (!ok) {
				return;
			}
		}
		this.isEditingMetadata = false;
		this.Api.put(`/sso/sps/${this.sp.id}/metarefresh`, {
			metadata: this.saml.metadata,
		});
	}

	editNotes() {
		this.noteInputText = this.sp.notes;
	}

	saveNotes() {
		this.Api.patch(`/sso/sps/${this.sp.id}`, { notes: this.noteInputText });
		this.sp.notes = this.noteInputText;
		this.noteInputText = undefined;
	}
}
